import { useState } from "react";
import { FileMinus2, X } from "lucide-react";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

export default function CreditNoteModal({ invoice, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [concept, setConcept] = useState("Devolución o descuento");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const originalTotal = Number(invoice.total || 0);

  const setFull = () => setAmount(originalTotal.toFixed(2));
  const setHalf = () => setAmount((originalTotal / 2).toFixed(2));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const value = Number(amount);
      if (!value || value <= 0) {
        setError("Ingresa un monto mayor a cero.");
        setBusy(false);
        return;
      }
      const response = await api.post(`/invoices/sent/${invoice.id}/credit-note`, {
        amount: value,
        concept,
        reason,
      }, { withCredentials: true });
      onCreated(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos crear la nota de crédito.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cash-modal-backdrop" onClick={onClose} data-testid="credit-note-modal">
      <div className="cash-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(520px, 96vw)" }}>
        <header>
          <div>
            <small>NOTA DE CRÉDITO</small>
            <b>Sobre {invoice.folio}</b>
          </div>
          <button type="button" onClick={onClose} data-testid="credit-note-close" aria-label="Cerrar"><X size={18} /></button>
        </header>
        <form onSubmit={submit}>
          <div style={{ padding: "0 24px 16px" }}>
            <div className="cancel-invoice-warn credit-note-hint" data-testid="credit-note-info">
              <FileMinus2 size={16} />
              <span>Emite una nota de crédito (CFDI Egreso <b>tipo 01</b>) para devolver o bonificar parte del pago al cliente. Se relaciona automáticamente con esta factura.</span>
            </div>

            <div className="credit-note-summary" data-testid="credit-note-summary">
              <div><small>Cliente</small><b>{invoice.receiver_name}</b></div>
              <div><small>Factura original</small><b>{money.format(originalTotal)}</b></div>
            </div>

            <label style={{ display: "block", marginTop: 12 }}>
              Concepto
              <input
                type="text"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                required
                data-testid="credit-note-concept"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", marginTop: 6 }}
              />
            </label>

            <label style={{ display: "block", marginTop: 12 }}>
              Monto a bonificar (máx. {money.format(originalTotal)})
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={originalTotal}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  data-testid="credit-note-amount"
                  style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
                />
                <button type="button" onClick={setHalf} className="cash-btn ghost" data-testid="credit-note-half">50%</button>
                <button type="button" onClick={setFull} className="cash-btn ghost" data-testid="credit-note-full">Total</button>
              </div>
            </label>

            <label style={{ display: "block", marginTop: 12 }}>
              Motivo (opcional)
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Ej: Cliente devolvió parte del pedido, descuento por retraso..."
                data-testid="credit-note-reason"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", marginTop: 6, resize: "vertical" }}
              />
            </label>

            {error && <p className="profile-error" data-testid="credit-note-error" style={{ marginTop: 10 }}>{error}</p>}
          </div>

          <div className="cash-modal-actions">
            <button type="button" onClick={onClose} className="cash-btn ghost" data-testid="credit-note-abort">Volver</button>
            <button type="submit" className="cash-btn primary" disabled={busy} data-testid="credit-note-confirm">
              {busy ? "Emitiendo..." : "Emitir nota de crédito"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
